import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/constants";

const TIMES = [
  "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
  "13:00", "13:30", "17:00", "17:30", "18:00", "18:30",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30",
];

const OCCASIONS = ["—", "Birthday", "Anniversary", "Date Night", "Business", "Brunch"];

export default function Reservation() {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    party_size: 2,
    date: today,
    time: "19:00",
    occasion: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        party_size: Number(form.party_size),
        occasion: form.occasion === "—" ? null : form.occasion || null,
        notes: form.notes || null,
      };
      const res = await axios.post(`${API}/reservations`, payload);
      setConfirmed(res.data);
      toast.success("Reservation requested", {
        description: "We'll confirm by email shortly.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong — please try again.";
      toast.error("Could not submit", { description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reserve"
      data-testid="reservation-section"
      className="bg-[#1A1713] text-[#FDFBF7] py-24 md:py-36"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <p className="font-mono-accent text-[12px] text-[#C98A4B] mb-5">
              — A Table for You
            </p>
            <h2
              data-testid="reserve-heading"
              className="font-serif-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight"
            >
              Reserve.
            </h2>
            <p className="mt-7 text-[#FDFBF7]/75 max-w-md leading-relaxed">
              Parties up to twelve booked online. For private events, the long
              table, or full-buyouts, write to{" "}
              <a
                href="mailto:hello@ladiosas.co"
                className="text-[#C98A4B] brass-link"
              >
                hello@ladiosas.co
              </a>
              .
            </p>
            <div className="mt-12 border-t border-[#FDFBF7]/15 pt-8 space-y-3 text-[#FDFBF7]/70 text-sm">
              <p className="font-mono-accent text-[12px] text-[#C98A4B] mb-2">House Notes</p>
              <p>· Late arrivals released after 15 minutes.</p>
              <p>· Children's portions available on request.</p>
              <p>· We cancel free up to 4 hours before service.</p>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            {confirmed ? (
              <div
                data-testid="reservation-confirmation"
                className="border border-[#C98A4B] p-8 md:p-12"
              >
                <p className="font-mono-accent text-[12px] text-[#C98A4B] mb-4">
                  — Confirmation #{confirmed.id.slice(0, 8).toUpperCase()}
                </p>
                <h3 className="font-serif-display text-4xl md:text-5xl mb-6">
                  Merci, {confirmed.name.split(" ")[0]}.
                </h3>
                <p className="text-[#FDFBF7]/80 leading-relaxed mb-8">
                  We've received your request for a table of{" "}
                  <em>{confirmed.party_size}</em> on{" "}
                  <em>
                    {confirmed.date} at {confirmed.time}
                  </em>
                  . A confirmation will arrive at{" "}
                  <em>{confirmed.email}</em> shortly.
                </p>
                <button
                  onClick={() => setConfirmed(null)}
                  data-testid="reservation-new-btn"
                  className="font-mono-accent text-[13px] border border-[#FDFBF7]/40 px-7 py-4 hover:border-[#FDFBF7] transition-colors duration-500"
                >
                  Make Another Reservation
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="reservation-form"
                className="grid grid-cols-2 gap-x-8 gap-y-7"
              >
                <Field label="Date" htmlFor="date" span="col-span-2 md:col-span-1">
                  <input
                    id="date"
                    type="date"
                    min={today}
                    required
                    data-testid="res-form-date"
                    value={form.date}
                    onChange={update("date")}
                    className="ldo-input"
                  />
                </Field>
                <Field label="Time" htmlFor="time" span="col-span-2 md:col-span-1">
                  <select
                    id="time"
                    required
                    data-testid="res-form-time"
                    value={form.time}
                    onChange={update("time")}
                    className="ldo-input bg-transparent"
                  >
                    {TIMES.map((t) => (
                      <option key={t} value={t} className="text-[#1A1713]">
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Party Size" htmlFor="party" span="col-span-2 md:col-span-1">
                  <input
                    id="party"
                    type="number"
                    min={1}
                    max={12}
                    required
                    data-testid="res-form-party"
                    value={form.party_size}
                    onChange={update("party_size")}
                    className="ldo-input"
                  />
                </Field>
                <Field label="Occasion" htmlFor="occasion" span="col-span-2 md:col-span-1">
                  <select
                    id="occasion"
                    data-testid="res-form-occasion"
                    value={form.occasion}
                    onChange={update("occasion")}
                    className="ldo-input bg-transparent"
                  >
                    {OCCASIONS.map((o) => (
                      <option key={o} value={o} className="text-[#1A1713]">
                        {o}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Full Name" htmlFor="name" span="col-span-2">
                  <input
                    id="name"
                    type="text"
                    required
                    data-testid="res-form-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Eleanor Salazar"
                    className="ldo-input"
                  />
                </Field>
                <Field label="Email" htmlFor="email" span="col-span-2 md:col-span-1">
                  <input
                    id="email"
                    type="email"
                    required
                    data-testid="res-form-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="ldo-input"
                  />
                </Field>
                <Field label="Phone" htmlFor="phone" span="col-span-2 md:col-span-1">
                  <input
                    id="phone"
                    type="tel"
                    required
                    data-testid="res-form-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(713) 555-0142"
                    className="ldo-input"
                  />
                </Field>
                <Field label="Notes (allergies, seating…)" htmlFor="notes" span="col-span-2">
                  <textarea
                    id="notes"
                    rows={3}
                    data-testid="res-form-notes"
                    value={form.notes}
                    onChange={update("notes")}
                    className="ldo-input resize-none"
                  />
                </Field>

                <div className="col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="res-form-submit"
                    className="w-full md:w-auto font-mono-accent text-[13px] bg-[#C98A4B] text-[#1A1713] px-10 py-5 hover:bg-[#FDFBF7] transition-colors duration-500 disabled:opacity-50"
                  >
                    {submitting ? "Sending…" : "Request a Table"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .ldo-input {
          width: 100%;
          background: transparent;
          color: #FDFBF7;
          border: 0;
          border-bottom: 1px solid rgba(253,251,247,0.25);
          padding: 12px 0 10px 0;
          font-family: 'DM Sans', sans-serif;
          font-size: 16px;
          outline: none;
          transition: border-color .35s ease;
        }
        .ldo-input::placeholder { color: rgba(253,251,247,0.35); }
        .ldo-input:focus { border-bottom-color: #C98A4B; }
        .ldo-input option { background:#1A1713; }
      `}</style>
    </section>
  );
}

function Field({ label, htmlFor, span = "col-span-2", children }) {
  return (
    <div className={span}>
      <label
        htmlFor={htmlFor}
        className="block font-mono-accent text-[12px] text-[#C98A4B] mb-1"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
